import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { 
  BarChart3, 
  Download, 
  RefreshCw, 
  TrendingUp, 
  TrendingDown, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Users,
  Calendar,
  Filter,
  Eye,
  PieChart,
  LineChart,
  Activity,
  Target,
  Zap,
  Database
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart as RechartsLineChart, Line, PieChart as RechartsPieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import DocumentUploadsService from "@/api/documentUploads.service";
import ExceptionQueueService from "@/api/exceptionQueue.service";
import ApprovalWorkflowsService from "@/api/approvalWorkflows.service";
import { DateRange } from "react-day-picker";
import { addDays, format } from "date-fns";

interface ProcessingReport {
  totalDocuments: number;
  processedDocuments: number;
  failedDocuments: number;
  pendingDocuments: number;
  averageProcessingTime: number;
  successRate: number;
  totalProcessingTime: number;
  documentsPerHour: number;
  peakProcessingHour: string;
  mostCommonDocumentType: string;
  errorRate: number;
  exceptionsGenerated: number;
  approvalsRequired: number;
  autoApprovedDocuments: number;
}

interface DocumentTypeMetrics {
  documentType: string;
  totalCount: number;
  successCount: number;
  failureCount: number;
  averageProcessingTime: number;
  averageConfidence: number;
  successRate: number;
}

interface ProcessingTrend {
  date: string;
  uploaded: number;
  processed: number;
  failed: number;
  avgProcessingTime: number;
}

interface ErrorAnalytics {
  errorType: string;
  count: number;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
  lastOccurrence: string;
}

interface UserActivity {
  userId: number;
  userName: string;
  documentsUploaded: number;
  documentsProcessed: number;
  exceptionsResolved: number;
  approvalsCompleted: number;
  lastActivity: string;
}

export default function DocumentProcessingReports() {
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState("overview");
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: addDays(new Date(), -30),
    to: new Date(),
  });
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>("all");
  const [selectedUser, setSelectedUser] = useState<string>("all");
  const [reportPeriod, setReportPeriod] = useState<string>("30d");
  
  // Data states
  const [processingReport, setProcessingReport] = useState<ProcessingReport | null>(null);
  const [documentTypeMetrics, setDocumentTypeMetrics] = useState<DocumentTypeMetrics[]>([]);
  const [processingTrends, setProcessingTrends] = useState<ProcessingTrend[]>([]);
  const [errorAnalytics, setErrorAnalytics] = useState<ErrorAnalytics[]>([]);
  const [userActivity, setUserActivity] = useState<UserActivity[]>([]);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchAllReports();
    }
  }, [selectedBusiness, dateRange, selectedDocumentType, selectedUser, reportPeriod]);

  const fetchAllReports = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchProcessingReport(),
        fetchDocumentTypeMetrics(),
        fetchProcessingTrends(),
        fetchErrorAnalytics(),
        fetchUserActivity(),
        fetchDocumentTypes(),
        fetchUsers()
      ]);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch reports data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessingReport = async () => {
    try {
      const startDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined;
      const endDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined;
      
      const response = await DocumentUploadsService.getProcessingAnalytics(selectedBusiness.id, startDate, endDate);
      if (response.data.result === "success") {
        setProcessingReport(response.data.report);
      }
    } catch (error) {
      console.error("Failed to fetch processing report:", error);
      // Mock data for development
      setProcessingReport({
        totalDocuments: 2847,
        processedDocuments: 2734,
        failedDocuments: 89,
        pendingDocuments: 24,
        averageProcessingTime: 45.2,
        successRate: 0.96,
        totalProcessingTime: 123456,
        documentsPerHour: 118.6,
        peakProcessingHour: "14:00",
        mostCommonDocumentType: "invoice",
        errorRate: 0.031,
        exceptionsGenerated: 156,
        approvalsRequired: 89,
        autoApprovedDocuments: 2645
      });
    }
  };

  const fetchDocumentTypeMetrics = async () => {
    try {
      const response = await DocumentUploadsService.getDocumentTypeBreakdown(selectedBusiness.id);
      if (response.data.result === "success") {
        setDocumentTypeMetrics(response.data.metrics);
      }
    } catch (error) {
      console.error("Failed to fetch document type metrics:", error);
      // Mock data for development
      setDocumentTypeMetrics([
        {
          documentType: "Invoice",
          totalCount: 1247,
          successCount: 1198,
          failureCount: 49,
          averageProcessingTime: 42.3,
          averageConfidence: 0.94,
          successRate: 0.96
        },
        {
          documentType: "Receipt",
          totalCount: 856,
          successCount: 834,
          failureCount: 22,
          averageProcessingTime: 38.7,
          averageConfidence: 0.91,
          successRate: 0.97
        },
        {
          documentType: "Bill",
          totalCount: 523,
          successCount: 505,
          failureCount: 18,
          averageProcessingTime: 51.2,
          averageConfidence: 0.89,
          successRate: 0.97
        },
        {
          documentType: "Contract",
          totalCount: 221,
          successCount: 197,
          failureCount: 24,
          averageProcessingTime: 78.4,
          averageConfidence: 0.82,
          successRate: 0.89
        }
      ]);
    }
  };

  const fetchProcessingTrends = async () => {
    try {
      const response = await DocumentUploadsService.getProcessingTrends(selectedBusiness.id, 'daily');
      if (response.data.result === "success") {
        setProcessingTrends(response.data.trends);
      }
    } catch (error) {
      console.error("Failed to fetch processing trends:", error);
      // Mock data for development
      const mockTrends = [];
      for (let i = 29; i >= 0; i--) {
        const date = addDays(new Date(), -i);
        mockTrends.push({
          date: format(date, 'MMM dd'),
          uploaded: Math.floor(Math.random() * 100) + 50,
          processed: Math.floor(Math.random() * 95) + 45,
          failed: Math.floor(Math.random() * 10) + 1,
          avgProcessingTime: Math.floor(Math.random() * 30) + 30
        });
      }
      setProcessingTrends(mockTrends);
    }
  };

  const fetchErrorAnalytics = async () => {
    try {
      const response = await DocumentUploadsService.getErrorAnalytics(selectedBusiness.id);
      if (response.data.result === "success") {
        setErrorAnalytics(response.data.errors);
      }
    } catch (error) {
      console.error("Failed to fetch error analytics:", error);
      // Mock data for development
      setErrorAnalytics([
        {
          errorType: "Low Confidence",
          count: 89,
          percentage: 45.2,
          trend: 'down',
          lastOccurrence: "2024-01-15T10:30:00Z"
        },
        {
          errorType: "OCR Failure",
          count: 34,
          percentage: 17.3,
          trend: 'stable',
          lastOccurrence: "2024-01-15T09:15:00Z"
        },
        {
          errorType: "Format Not Supported",
          count: 28,
          percentage: 14.2,
          trend: 'up',
          lastOccurrence: "2024-01-15T11:45:00Z"
        },
        {
          errorType: "Validation Failed",
          count: 23,
          percentage: 11.7,
          trend: 'down',
          lastOccurrence: "2024-01-15T08:20:00Z"
        },
        {
          errorType: "Timeout",
          count: 23,
          percentage: 11.7,
          trend: 'stable',
          lastOccurrence: "2024-01-15T12:10:00Z"
        }
      ]);
    }
  };

  const fetchUserActivity = async () => {
    try {
      // This would be a new endpoint for user activity analytics
      const response = await DocumentUploadsService.getUserActivity(selectedBusiness.id);
      if (response.data.result === "success") {
        setUserActivity(response.data.activity);
      }
    } catch (error) {
      console.error("Failed to fetch user activity:", error);
      // Mock data for development
      setUserActivity([
        {
          userId: 1,
          userName: "John Smith",
          documentsUploaded: 234,
          documentsProcessed: 228,
          exceptionsResolved: 12,
          approvalsCompleted: 45,
          lastActivity: "2024-01-15T14:30:00Z"
        },
        {
          userId: 2,
          userName: "Sarah Johnson",
          documentsUploaded: 189,
          documentsProcessed: 185,
          exceptionsResolved: 8,
          approvalsCompleted: 32,
          lastActivity: "2024-01-15T13:45:00Z"
        },
        {
          userId: 3,
          userName: "Mike Davis",
          documentsUploaded: 156,
          documentsProcessed: 152,
          exceptionsResolved: 15,
          approvalsCompleted: 28,
          lastActivity: "2024-01-15T12:20:00Z"
        }
      ]);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      // Fetch document types for filtering
      setDocumentTypes([
        { id: 1, name: "Invoice" },
        { id: 2, name: "Receipt" },
        { id: 3, name: "Bill" },
        { id: 4, name: "Contract" }
      ]);
    } catch (error) {
      console.error("Failed to fetch document types:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      // Fetch users for filtering
      setUsers([
        { id: 1, name: "John Smith" },
        { id: 2, name: "Sarah Johnson" },
        { id: 3, name: "Mike Davis" }
      ]);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleExportReport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      const startDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined;
      const endDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined;
      
      const response = await DocumentUploadsService.exportProcessingReport(
        selectedBusiness.id, 
        format, 
        {
          startDate,
          endDate,
          documentType: selectedDocumentType,
          userId: selectedUser
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 
              format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
              'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `processing_report_${format(new Date(), 'yyyy-MM-dd')}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Success",
        description: `Report exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export report",
        variant: "destructive",
      });
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-red-500" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-green-500" />;
      default: return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Less than 1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="h-8 w-8" />
            Document Processing Reports
          </h1>
          <p className="text-muted-foreground">
            Comprehensive analytics and insights for AI document processing
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExportReport('pdf')}>
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" onClick={() => handleExportReport('excel')}>
            <Download className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
          <Button onClick={fetchAllReports} disabled={loading}>
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Report Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Date Range</Label>
              <DatePickerWithRange
                date={dateRange}
                onDateChange={setDateRange}
              />
            </div>
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={selectedDocumentType} onValueChange={setSelectedDocumentType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {documentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name.toLowerCase()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>User</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Period</Label>
              <Select value={reportPeriod} onValueChange={setReportPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value="1y">Last year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reports Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="document-types">Document Types</TabsTrigger>
          <TabsTrigger value="errors">Error Analysis</TabsTrigger>
          <TabsTrigger value="users">User Activity</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {processingReport && (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Documents</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{processingReport.totalDocuments.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">
                      {processingReport.documentsPerHour.toFixed(1)} per hour
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
                    <CheckCircle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(processingReport.successRate * 100).toFixed(1)}%</div>
                    <Progress value={processingReport.successRate * 100} className="mt-2" />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Avg Processing Time</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatDuration(processingReport.averageProcessingTime)}</div>
                    <p className="text-xs text-muted-foreground">
                      Peak at {processingReport.peakProcessingHour}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Exceptions</CardTitle>
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{processingReport.exceptionsGenerated}</div>
                    <p className="text-xs text-muted-foreground">
                      {(processingReport.errorRate * 100).toFixed(1)}% error rate
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Processing Trends Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Processing Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={processingTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="uploaded" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                      <Area type="monotone" dataKey="processed" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                      <Area type="monotone" dataKey="failed" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Processing Time Trends */}
            <Card>
              <CardHeader>
                <CardTitle>Processing Time Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsLineChart data={processingTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="avgProcessingTime" stroke="#3b82f6" strokeWidth={2} />
                  </RechartsLineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Document Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Document Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={[
                        { name: 'Processed', value: processingReport?.processedDocuments || 0, color: '#10b981' },
                        { name: 'Failed', value: processingReport?.failedDocuments || 0, color: '#ef4444' },
                        { name: 'Pending', value: processingReport?.pendingDocuments || 0, color: '#f59e0b' }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {[
                        { name: 'Processed', value: processingReport?.processedDocuments || 0, color: '#10b981' },
                        { name: 'Failed', value: processingReport?.failedDocuments || 0, color: '#ef4444' },
                        { name: 'Pending', value: processingReport?.pendingDocuments || 0, color: '#f59e0b' }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Document Types Tab */}
        <TabsContent value="document-types" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Document Type Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {documentTypeMetrics.map((metric, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{metric.documentType}</h4>
                      <Badge variant="outline">
                        {(metric.successRate * 100).toFixed(1)}% success
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Total</p>
                        <p className="font-medium">{metric.totalCount}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Avg Time</p>
                        <p className="font-medium">{formatDuration(metric.averageProcessingTime)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Avg Confidence</p>
                        <p className="font-medium">{(metric.averageConfidence * 100).toFixed(1)}%</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Failures</p>
                        <p className="font-medium text-red-600">{metric.failureCount}</p>
                      </div>
                    </div>
                    <Progress value={metric.successRate * 100} className="mt-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Error Analysis Tab */}
        <TabsContent value="errors" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Error Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {errorAnalytics.map((error, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="font-medium">{error.errorType}</p>
                        <p className="text-sm text-muted-foreground">
                          Last occurred: {formatTimeAgo(error.lastOccurrence)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-medium">{error.count}</p>
                        <p className="text-sm text-muted-foreground">{error.percentage}%</p>
                      </div>
                      {getTrendIcon(error.trend)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Activity Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>User Activity Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {userActivity.map((user, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium">{user.userName}</p>
                          <p className="text-sm text-muted-foreground">
                            Last active: {formatTimeAgo(user.lastActivity)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Uploaded</p>
                        <p className="font-medium">{user.documentsUploaded}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Processed</p>
                        <p className="font-medium">{user.documentsProcessed}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Exceptions Resolved</p>
                        <p className="font-medium">{user.exceptionsResolved}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Approvals</p>
                        <p className="font-medium">{user.approvalsCompleted}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 